import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  ShieldCheck,
  Users,
  HeartPulse,
  Building2,
  MessageCircle,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import BreadcrumbSchema from "../../components/public/BreadcrumbSchema";
import AccessModelCallout from "../../components/public/AccessModelCallout";

/**
 * Public — Healthcare service page.
 * Route: /services/healthcare-hospice
 *
 * IMAGE ASSETS
 * Images served locally from /public/images/healthcare/ (no external hotlinking).
 * Sources documented in the report to stakeholders.
 */
const IMAGES = {
  hero: {
    src: "/images/healthcare/hero.jpg",
    alt: "A clinician in a white lab coat in a warm, supportive conversation with a colleague inside a clinical office.",
  },
  palliative: {
    src: "/images/healthcare/palliative-hands.jpg",
    alt: "Two hands gently clasped together in a quiet, dignified moment of support.",
  },
};

const HUMAN_PRESSURES = [
  "Repeated exposure to illness, decline, death, and grief",
  "Compassion fatigue and emotional exhaustion",
  "Difficult patient or family encounters",
  "Moral and emotional strain surrounding complex situations",
  "High-acuity or demanding work environments",
  "Long shifts and irregular schedules",
  "Workplace conflict or difficult team dynamics",
  "Family and relationship concerns",
  "Financial pressures",
  "Caregiving responsibilities",
  "Personal illness or serious diagnoses",
  "Grief and loss outside the workplace",
  "Career transitions and workplace changes",
  "Spiritual or meaning-of-life concerns",
  "Or simply needing a confidential person who will listen",
];

const COMPLEMENTS = [
  "Human Resources",
  "Employee Assistance Programs",
  "Mental-health and counseling benefits",
  "Hospital staff chaplains",
  "Hospice spiritual-care coordinators",
  "Clinical social workers",
  "Employee wellness programs",
  "Internal pastoral or spiritual-care departments",
  "Medical professionals",
  "An employee's personal clergy or faith community",
  "Emergency services",
];

const HEALTHCARE_SETTINGS = [
  "Hospitals",
  "Hospice & Palliative Care",
  "Home Health",
  "Outpatient Clinics",
  "Rehabilitation Organizations",
  "Healthcare Practices",
  "Multi-Site Healthcare Organizations",
  "Behavioral Health Organizations within non-clinical chaplain scope",
  "Other healthcare environments seeking confidential workforce support",
];

const ENVIRONMENT_LIST = [
  "A hospital with multiple departments and shifts",
  "A hospice interdisciplinary team",
  "Home-health employees working throughout a service region",
  "An outpatient or specialty practice",
  "A rehabilitation environment",
  "A behavioral-health organization within non-clinical chaplain scope",
  "A multi-site healthcare organization",
];

function SectionHeading({ eyebrow, children }) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
          {eyebrow}
        </p>
      )}
      <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
        {children}
      </h2>
    </div>
  );
}

export default function HealthcarePage() {
  usePageMeta({
    title:
      "Healthcare Workplace Chaplaincy — Hospitals, Hospice, Home Health & Clinics | California Corporate Chaplains",
    description:
      "Confidential, non-clinical workplace chaplaincy for healthcare employees, hospice and palliative-care teams, home health, clinics, rehabilitation, and multi-site healthcare organizations. 24/7 on-call chaplain access included.",
  });

  return (
    <article className="bg-white" data-testid="service-detail-healthcare-hospice">
      <BreadcrumbSchema
        id="healthcare-hospice"
        items={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: "Healthcare & Hospice", path: "/services/healthcare-hospice" },
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-10 md:pb-16">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8"
            data-testid="hc-back-to-services"
          >
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
                Service · Healthcare
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
                Workplace Chaplain Care for Healthcare Teams
              </h1>
              <p className="mt-5 text-xl sm:text-2xl text-slate-700 font-heading leading-snug">
                Supporting the People Who Spend Their Days Caring for Others
              </p>

              <div className="mt-8 space-y-5 text-base sm:text-lg text-slate-700 leading-relaxed">
                <p>
                  Healthcare professionals regularly enter some of the most
                  difficult moments in another person's life.
                </p>
                <p>
                  Nurses, physicians, aides, technicians, hospice and
                  palliative-care professionals, home-health clinicians,
                  administrative staff, and healthcare leaders work in
                  environments where illness, uncertainty, difficult decisions,
                  grief, and loss are part of the work.
                </p>
                <p>
                  At the same time, they are still carrying their own lives —
                  families, relationships, caregiving responsibilities,
                  financial pressures, health concerns, grief, and personal
                  challenges.
                </p>
                <p>
                  California Corporate Chaplains provides confidential,
                  non-clinical workplace chaplain support for the people behind
                  the care.
                </p>
                <p>
                  Through scheduled workplace presence, remote support, and
                  24/7 on-call chaplain access, CCCHAPS gives healthcare
                  employees and leaders a trusted person outside normal
                  clinical and supervisory relationships who can listen,
                  support, and remain present when life or work becomes heavy.
                </p>
              </div>

              <div className="mt-8 rounded-2xl border border-blue-100 bg-[#F0F4F8] px-6 py-5">
                <p className="text-base sm:text-lg text-slate-800 leading-relaxed">
                  Chaplain support is voluntary, confidential within
                  appropriate professional, ethical, safety, and legal limits,
                  and respectful of belief and non-belief.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/schedule?type=healthcare_hospice"
                  data-testid="hc-hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
                >
                  Request a Healthcare Consultation{" "}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/packages"
                  data-testid="hc-hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
                >
                  Build a Care Package
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div
                className="aspect-[5/6] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="hc-image-slot-hero"
              >
                <img
                  src={IMAGES.hero.src}
                  alt={IMAGES.hero.alt}
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* The Human Pressures Healthcare Teams Carry */}
        <section className="mt-4 md:mt-6">
          <SectionHeading eyebrow="What healthcare teams carry">
            The Human Pressures Healthcare Teams Carry
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed mb-6">
            <p>
              Healthcare work requires clinical skill, attention, compassion,
              and responsibility — often simultaneously.
            </p>
            <p>
              The difficult part is that the emotional impact of the work does
              not always end when a shift ends.
            </p>
            <p>Healthcare employees may carry concerns involving:</p>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="hc-human-pressures"
          >
            {HUMAN_PRESSURES.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 space-y-3 text-base text-slate-700 leading-relaxed">
            <p>A chaplain does not need to solve every concern.</p>
            <p>
              Sometimes the most important first step is providing a safe
              place for an employee to say what they have been carrying.
            </p>
          </div>
        </section>

        {/* How Workplace Chaplaincy Works in Healthcare */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="How it works">
            How Workplace Chaplaincy Works in Healthcare
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Healthcare environments do not operate like traditional
              nine-to-five workplaces.
            </p>
            <p>
              Support has to fit around shifts, patient-care responsibilities,
              multiple departments, mobile teams, and facilities that may
              operate around the clock.
            </p>
            <p>
              CCCHAPS builds chaplain support around the organization's
              selected Care Team Package.
            </p>
            <p>
              Depending on the arrangement, this may include scheduled on-site
              presence, remote support, confidential messaging, phone and text
              access, scheduled video when available, and 24/7 on-call chaplain
              support.
            </p>
            <p>
              During scheduled workplace visits, chaplains can become a
              familiar and approachable presence rather than someone employees
              meet for the first time during a crisis.
            </p>
            <p>Employees choose whether they want to engage.</p>
            <p>
              A conversation may happen during a break, following a difficult
              case, after receiving personal news, or simply because an
              employee needs someone outside the normal chain of command to
              talk with.
            </p>
            <p>
              Through CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On
              Demand, employees also have pathways to support and resources
              beyond the scheduled workplace visit.
            </p>
          </div>

          <AccessModelCallout
            variant="healthcare"
            className="mt-10"
            testId="hc-access-model-callout"
          />
        </section>

        {/* Support Before, During, and After Difficult Moments */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Difficult moments">
            Support Before, During, and After Difficult Moments
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Healthcare professionals frequently care for others during
              moments that may carry significant emotional weight.
            </p>
            <p>A patient may decline unexpectedly.</p>
            <p>A longtime patient may die.</p>
            <p>A family interaction may be particularly difficult.</p>
            <p>
              An employee may learn that someone in their own family is
              seriously ill.
            </p>
            <p>A team may experience the death of a colleague.</p>
            <p>
              A leader may have to guide employees through difficult news
              while also processing the event personally.
            </p>
            <p>
              CCCHAPS chaplains can provide a calm, confidential, non-clinical
              presence during these situations and remain available for
              appropriate follow-up afterward.
            </p>
            <p>
              The goal is not to remove difficult emotions or rush people
              through grief.
            </p>
            <p>
              It is to make sure employees and leaders have somewhere
              appropriate to turn while they navigate what has happened.
            </p>
          </div>
        </section>

        {/* Hospice & Palliative Care */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Hospice · Palliative · End-of-life">
            A Particular Strength in Hospice, Palliative Care, and End of Life
          </SectionHeading>
          <div className="grid md:grid-cols-5 gap-8 items-start">
            <div className="md:col-span-3 space-y-5 text-base text-slate-700 leading-relaxed">
              <p>
                Grief, serious illness, caregiving, hospice, and end-of-life
                support are important areas of CCCHAPS chaplaincy practice.
              </p>
              <p>
                Hospice and palliative-care professionals repeatedly accompany
                patients and families through decline, anticipatory grief,
                dying, and bereavement. Even when this work is deeply
                meaningful, repeated exposure to loss can carry an emotional
                weight.
              </p>
              <p>CCCHAPS can support the professionals doing that work.</p>
              <p>
                Where an organization's service arrangement specifically
                includes patient and family support, CCCHAPS chaplains may
                also provide ministry of presence, companionship, non-clinical
                emotional support, spiritual support when requested, and
                appropriate grief or end-of-life presence within professional
                chaplain scope.
              </p>
              <p>
                This service is structured carefully so that CCCHAPS
                complements rather than disrupts existing hospice, hospital,
                pastoral-care, social-work, or spiritual-care programs.
              </p>
            </div>
            <div className="md:col-span-2">
              <div
                className="aspect-[4/5] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="hc-image-slot-palliative"
              >
                <img
                  src={IMAGES.palliative.src}
                  alt={IMAGES.palliative.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Leaders and HR */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Leaders & HR">
            Support for Healthcare Leaders and HR Teams
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              The people leading healthcare organizations carry pressures of
              their own.
            </p>
            <p>
              Executives, clinical leaders, supervisors, administrators, and
              HR professionals may simultaneously be managing staffing
              concerns, difficult employee situations, organizational change,
              patient-care responsibilities, workplace incidents, and their
              own personal lives.
            </p>
            <p>
              There are also moments when a leader knows an employee needs
              support but recognizes that the conversation should not remain
              solely within management or HR.
            </p>
            <p>A workplace chaplain provides another appropriate pathway.</p>
            <p>
              CCCHAPS chaplains can offer confidential support directly to
              leaders while also serving as a neutral support resource for
              employees.
            </p>
            <p>
              When an employee needs assistance beyond the chaplain's scope,
              the chaplain can encourage appropriate connection with
              resources such as an EAP, licensed mental-health professional,
              healthcare provider, community resource, or other specialized
              service.
            </p>
          </div>
        </section>

        {/* Working alongside */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Complementary care">
            Working Alongside the Support Systems You Already Have
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed mb-6">
            <p>
              CCCHAPS is not designed to replace existing healthcare support
              structures.
            </p>
            <p>Our workplace chaplains complement resources such as:</p>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="hc-complements"
          >
            {COMPLEMENTS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-base sm:text-lg font-medium text-slate-900 leading-relaxed">
            CCCHAPS provides an additional relational layer of care: a
            trusted, confidential, non-clinical person employees and leaders
            can choose to talk with.
          </p>
        </section>

        {/* Professional boundaries */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Boundaries">
            Professional Boundaries Matter
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>Healthcare environments require clear professional roles.</p>
            <p>
              CCCHAPS chaplains do not diagnose mental-health conditions,
              provide psychotherapy, practice medicine, make clinical
              decisions, or interfere with patient-care responsibilities.
            </p>
            <p>
              We also do not replace established hospital or hospice
              chaplaincy departments.
            </p>
            <p>Spiritual conversations occur when welcomed by the individual.</p>
            <p>
              Chaplain support is permission-based, and employees who decline
              a conversation are respected without pressure or unwanted
              pursuit.
            </p>
            <p>
              Personal conversations are treated as confidential within
              appropriate professional, ethical, safety, and legal limits.
            </p>
          </div>
        </section>

        {/* Beyond the scheduled visit */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="24/7 access">
            Support That Extends Beyond the Scheduled Visit
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              A difficult moment may happen at 2:00 in the afternoon — or 2:00
              in the morning.
            </p>
            <p>
              Every CCCHAPS service arrangement includes 24/7 on-call chaplain
              access.
            </p>
            <p>
              Employees and their immediate household members can access
              available support pathways through the organization's CCCHAPS
              arrangement, while CALLACHAP, the CCCHAPS Care Portal, and
              CCCHAPS On Demand extend access beyond scheduled workplace
              presence.
            </p>
            <p>
              Scheduled on-site, remote, or blended chaplain presence is then
              built around the organization's Care Team Package and operating
              environment.
            </p>
            <p>That might mean supporting:</p>
          </div>
          <ul
            className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="hc-environments"
          >
            {ENVIRONMENT_LIST.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Building2
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={1.75}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-base sm:text-lg font-medium text-slate-900 leading-relaxed">
            The model adapts to the workplace rather than expecting the
            workplace to adapt to the chaplain.
          </p>
        </section>

        {/* Healthcare settings */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Settings served">
            Healthcare Settings We Serve
          </SectionHeading>
          <p className="text-base text-slate-700 leading-relaxed mb-6">
            CCCHAPS workplace chaplaincy can be structured for:
          </p>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="hc-settings"
          >
            {HEALTHCARE_SETTINGS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <HeartPulse
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={1.75}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Closing */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Care for the caregivers">
            Care for the People Who Carry the Care
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Healthcare organizations depend on people who repeatedly show up
              for others.
            </p>
            <p>Those people need somewhere they can turn too.</p>
            <p>
              Workplace chaplaincy provides a confidential space outside the
              clinical and supervisory structure where an employee can talk,
              reflect, grieve, ask for spiritual support when desired, or
              simply be heard.
            </p>
            <p>
              For CCCHAPS, healthcare chaplaincy begins with a simple
              commitment:
            </p>
            <p className="mt-4 font-heading text-xl text-slate-900 leading-snug">
              Care for the people who spend their days caring for everyone
              else.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section
          className="mt-14 rounded-2xl bg-[#F0F4F8] border border-blue-100 p-6 md:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          data-testid="hc-final-cta"
        >
          <div>
            <h3 className="font-heading text-xl font-medium text-slate-900">
              Ready to Talk?
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              Whether you are considering workplace chaplaincy for a hospital,
              hospice, home-health organization, clinic, or healthcare system,
              we can discuss your workforce, existing support resources, and
              the type of chaplain presence that would fit your environment.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/schedule?type=healthcare_hospice"
              data-testid="hc-final-cta-schedule"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] whitespace-nowrap"
            >
              Request a Healthcare Consultation{" "}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}
